import PressureProcessingSettings from './PressureProcessingSettings';
import cv, { Point } from "@techstark/opencv-js";
import Line from "./Line"
import zoneProcessor from './zoneProcessor';
import PressureData from 'Scan/PressureData';


export default class FrameProcessorPressure {
  settings = new PressureProcessingSettings();
  pressureToIntScale = 100.0;
  sensorWidth: number = -1;
  sensorHeight: number = -1;

  constructor() {
    this.settings.ResetToDefault();
  }

  computeCOPGlobal = (parentMat: cv.Mat, roi: cv.Rect): cv.Point => {
    // 1) crop out the ROI
    const sub = parentMat.roi(
      new cv.Rect(
        roi.x,
        roi.y,
        roi.width,
        roi.height
      )
    );
    // 2) find the local COP
    const localCop = this.ComputeCOP(sub);
    // clean up the temporary sub-mat
    sub.delete();
    // 3) shift it BACK into the parent’s coordinate system
    return new cv.Point(localCop.x + roi.x, localCop.y + roi.y);
  };

  ComputeCOP = (frame: cv.Mat, roi?: cv.Rect): cv.Point => {
    let sum = 0;
    let sumWY = 0;
    let sumWX = 0;
    if (frame === undefined) return new cv.Point(-1, -1);

    var fw = frame.cols;
    var fh = frame.rows;
    var fx = 0;
    var fy = 0;

    if (roi) {
      fx = Math.round(roi.x);
      fy = Math.round(roi.y);
      fw = Math.round(roi.width + fx);
      fh = Math.round(roi.height + fy);
    }

    for (let x = fx; x < fw; x++) {
      for (let y = fy; y < fh; y++) {
        const fdata = frame.ucharPtr(y, x)[0];
        if (fdata > this.settings.COP_Computing_THR) {
          sumWY += (fdata * y);
          sumWX += (fdata * x);
          sum += fdata;
        }
      }
    }
    if (sum === 0) {
      return new cv.Point((fw - fx) / 2, (fh - fy) / 2);
    }
    else {
      return new cv.Point(sumWX / sum, sumWY / sum);
    }
  }

  getColSumsArray = (mat: cv.Mat): number[] => {
    const colSums = new Array(mat.cols).fill(0);
    for (let x = 0; x < mat.cols; x++) {
      let sum = 0;
      for (let y = 0; y < mat.rows; y++) {
        sum += mat.ucharPtr(y, x)[0];
      }
      colSums[x] = sum;
    }
    return colSums;
  }

  getRowSumsArray = (mat: cv.Mat): number[] => {
    const sums = new Array(mat.rows).fill(0);
    for (let h = 0; h < mat.rows; h++) {
      let s = 0;
      for (let w = 0; w < mat.cols; w++) {
        s += mat.ucharPtr(h, w)[0];
      }
      sums[h] = s;
    }
    return sums;
  };

  getRowSumsArrayLimited = (mat: cv.Mat, minCol: number, maxCol: number): number[] => {
    const sums = new Array(mat.rows).fill(0);
    for (let h = 0; h < mat.rows; h++) {
      let s = 0;
      for (let w = minCol; w <= maxCol; w++) {
        s += mat.ucharPtr(h, w)[0];
      }
      sums[h] = s;
    }
    return sums;
  };

  dilateMax = (arr: number[], k: number): number[] => {
    const half = Math.floor(k / 2);
    const out = new Array(arr.length).fill(0);
    for (let i = 0; i < arr.length; i++) {
      let maxVal = arr[i];
      for (let j = -half; j <= half; j++) {
        const idx = i + j;
        if (idx >= 0 && idx < arr.length) maxVal = Math.max(maxVal, arr[idx]);
      }
      out[i] = maxVal;
    }
    return out;
  };

  getTrimValues = (arr: number[]): [number, number] => {
    let minI = 0;
    let maxI = arr.length;
    let count = arr.length;
    const THR = this.settings.AutoCrop_SumTHR;
    for (let i = 0; i < count; i++) {
      if (arr[i] > THR) { minI = i; break; }
    }
    for (let i = count - 1; i > 0; i--) {
      if (arr[i] > THR) { maxI = i; break; }
    }
    return [minI, maxI];
  };

  findSeparationPoint(colSums: number[], threshold: number = 10): number {
    const dilatedCols = this.dilateMax(colSums, 5);
    let minSum = Math.min(...dilatedCols);
    let separation = Math.floor(colSums.length / 2);
    if (minSum < threshold) {
      for (let i = Math.floor(colSums.length / 4); i < colSums.length * 3 / 4; i++) {
        if (dilatedCols[i] === minSum) {
          separation = i;
          break;
        }
      }
    }
    return separation;
  }

  autoCropROI(mat: cv.Mat): cv.Rect {
    const AUTO_CROP_MARGIN = this.settings.AutoCrop_Margin;

    const colSums = this.getColSumsArray(mat);
    let [minCol, maxCol] = this.getTrimValues(colSums);

    const rowSums = this.getRowSumsArray(mat);
    let [minRow, maxRow] = this.getTrimValues(rowSums);

    let minX = minCol - AUTO_CROP_MARGIN;
    let maxX = maxCol + AUTO_CROP_MARGIN;
    let minY = minRow - AUTO_CROP_MARGIN;
    let maxY = maxRow + AUTO_CROP_MARGIN;

    if (minX < 0) minX = 0;
    if (minY < 0) minY = 0;
    if (maxX > mat.cols - 1) maxX = mat.cols - 1;
    if (maxY > mat.rows - 1) maxY = mat.rows - 1;

    return new cv.Rect(minX, minY, maxX - minX, maxY - minY);
  }

  autoCropROIRight = (mat: cv.Mat): { x: number; y: number; width: number; height: number } => {
    const colSums = this.getColSumsArray(mat);
    const dilatedCols = this.dilateMax(colSums, 3);
    const [minRow, maxRow] = this.getTrimValuesRight(dilatedCols);

    const rowSums = this.getRowSumsArrayLimited(mat, minRow, maxRow);
    const dilatedRows = this.dilateMax(rowSums, 3);
    const [minCol, maxCol] = this.getTrimValues(dilatedRows);

    let x = minRow - this.settings.AutoCrop_Margin;
    let y = minCol - this.settings.AutoCrop_Margin;
    let width = maxRow + this.settings.AutoCrop_Margin - x;
    let height = maxCol + this.settings.AutoCrop_Margin - y;

    x = Math.max(0, x);
    y = Math.max(0, y);
    width = Math.min(mat.cols - x, width);
    height = Math.min(mat.rows - y, height);

    return { x, y, width, height };
  }

  autoCropROILeft = (mat: cv.Mat): { x: number; y: number; width: number; height: number } => {
    const colSums = this.getColSumsArray(mat);
    const dilatedCols = this.dilateMax(colSums, 3);
    const [minRow, maxRow] = this.getTrimValuesLeft(dilatedCols);

    const rowSums = this.getRowSumsArrayLimited(mat, minRow, maxRow);
    const dilatedRows = this.dilateMax(rowSums, 3);
    const [minCol, maxCol] = this.getTrimValues(dilatedRows);

    let x = minRow - this.settings.AutoCrop_Margin;
    let y = minCol - this.settings.AutoCrop_Margin;
    let width = maxRow + this.settings.AutoCrop_Margin - x;
    let height = maxCol + this.settings.AutoCrop_Margin - y;

    x = Math.max(0, x);
    y = Math.max(0, y);
    width = Math.min(mat.cols - x, width);
    height = Math.min(mat.rows - y, height);

    return { x, y, width, height };
  }

  getTrimValuesRight = (arr: number[]): [number, number] => {
    let minCol = 0;
    let maxCol = arr.length;
    let count = arr.length;
    const THR = this.settings.AutoCrop_SumTHR;
    let flag = false;

    for (let i = count - 1; i > 0; i--) {
      if (flag === false) {
        if (arr[i] > THR) {
          maxCol = i;
          flag = true;
        }
      } else {
        if (arr[i] < THR) {
          minCol = i;
          break;
        }
      }
    }
    return [minCol + 1, maxCol];
  };

  getTrimValuesLeft = (arr: number[]): [number, number] => {
    let minCol = 0;
    let maxCol = arr.length;
    const THR = this.settings.AutoCrop_SumTHR;
    let flag = false;
    for (let i = 0; i < arr.length; i++) {
      if (!flag) {
        if (arr[i] > THR) {
          minCol = i;
          flag = true;
        }
      } else {
        if (arr[i] < THR) {
          maxCol = i;
          break;
        }
      }
    }
    return [minCol, maxCol - 1];
  };

  getAxisLine = (cops: cv.Point[], roi: cv.Rect, mat: cv.Mat): Line => {
    // fit line through cops...
    const x = cops.map(p => p.x);
    const y = cops.map(p => p.y);
    const { intercept, slope } = this.fitLine(x, y);

    const y0 = roi.y + roi.height;
    const x0 = (y0 - intercept) / slope;
    const y1 = roi.y;
    const x1 = (y1 - intercept) / slope;

    const untrimmed = new Line(new cv.Point(x0, y0), new cv.Point(x1, y1));

    // If trimAxisLine returns an object {start, end} too, just forward it:
    const trimmed = this.trimAxisLine(untrimmed, roi, mat);

    return trimmed;
  };

  getRealDistance = (point1: any, point2: any): number => {
    const pressureData = new PressureData();
    const rp1 = new cv.Point(point1.x * pressureData.PressureSensorWidth, point1.y * pressureData.PressureSensorHeight);
    const rp2 = new cv.Point(point2.x * pressureData.PressureSensorWidth, point2.y * pressureData.PressureSensorHeight);

    return Math.sqrt((rp1.x - rp2.x) * (rp1.x - rp2.x) + (rp1.y - rp2.y) * (rp1.y - rp2.y))
  }

  GetRealSizedPoint = (framePoint: cv.Point, roi: {x:number, y: number, width: number , height: number}) : {x:number, y: number} => {
    const pressureData = new PressureData();
    const zerox = roi.x + roi.width;
    const zeroy = roi.y;

    const center = {x:(zerox - framePoint.x) * pressureData.PressureSensorWidth , y: (framePoint.y - zeroy) * pressureData.PressureSensorHeight};
    return center;
  }

  getWidthLine = (axis: Line, roi: cv.Rect, frame: cv.Mat): Line => {
    const midPoint = this.pointByPercent(axis, 0.3)

    const lineDir = new Line();
    lineDir.calculateDirection(axis);
    const dir = lineDir.direction;
    const direction = new cv.Point(-dir.y, dir.x);
    const untrimmed = this.getLineTrimmedByRoi(direction, midPoint, roi);
    const trimmed = this.trimWidthLine(untrimmed, roi, frame);

    return trimmed;
  }

  getHeelLine = (axis: { start: cv.Point2f; end: cv.Point2f }, roi: cv.Rect, frame: cv.Mat): Line => {
    const midPoint = this.pointByPercent(axis, 0.85);

    const lineDir = new Line();
    lineDir.calculateDirection(axis);
    const dir = lineDir.direction;
    const direction = new cv.Point(-dir.y, dir.x);
    const untrimmed = this.getLineTrimmedByRoi(direction, midPoint, roi);
    const trimmed = this.trimWidthLine(untrimmed, roi, frame);

    return trimmed;
  }

  trimAxisLine(axis: Line, roi: cv.Rect, maxFrame: cv.Mat) {
    const lineValues = new Array(roi.height).fill(0);
    const perpDirection = new cv.Point(-axis.direction.y, axis.direction.x);
    for (let i = 0; i < roi.height; i++) {
      const curY = roi.y + i;
      const curPoint = new cv.Point(axis.xByY(curY), curY);
      lineValues[i] = this.getLineSumValue(maxFrame, curPoint, perpDirection, this.settings.AutoAxis_SumCount, roi);
    }
    const minmax = this.trimArray(lineValues, this.settings.AutoAxis_SumThr);

    const p1 = new cv.Point(axis.xByY(minmax.x + roi.y - 1), minmax.x + roi.y - 1);
    const p2 = new cv.Point(axis.xByY(minmax.y + roi.y + 1), minmax.y + roi.y + 1);

    return new Line(this.bringPointToROI(p1, roi), this.bringPointToROI(p2, roi));
  }

  getLineTrimmedByRoi = (direction: cv.Point2f, basePoint: cv.Point2f, roi: { x: number; y: number; width: number; height: number }): { start: cv.Point2f; end: cv.Point2f } => {
    const line = { start: new cv.Point(basePoint.x - direction.x * 100, basePoint.y - direction.y * 100), end: new cv.Point(basePoint.x + direction.x * 100, basePoint.y + direction.y * 100) };
    let pt: cv.Point, pb: cv.Point, pr: cv.Point, pl: cv.Point;
    pt = new cv.Point(-1, -1);
    pb = new cv.Point(-1, -1);
    pl = new cv.Point(-1, -1);
    pr = new cv.Point(-1, -1);
    let ps: cv.Point[] = [pt, pb, pl, pr];


    if (direction.x !== 0) {
      pr = this.pointByX(line, (roi.x + roi.width) - 0.1)
      pl = this.pointByX(line, roi.x + 0.1);
    }
    if (direction.y !== 0) {
      pt = this.pointByY(line, roi.y + 0.1);
      pb = this.pointByY(line, (roi.y + roi.height) - 0.1);
    }

    let i = 0
    if (this.isPointInsideROI(pr, roi)) {
      ps[i] = pr;
      i++;
    }
    if (this.isPointInsideROI(pl, roi)) {
      ps[i] = pl;
      i++;
    }
    if (this.isPointInsideROI(pt, roi)) {
      ps[i] = pt;
      i++;
    }
    if (this.isPointInsideROI(pb, roi)) {
      ps[i] = pb;
      i++;
    }

    return { start: ps[0], end: ps[1] }
  };

  trimWidthLine = (untrimmed: { start: cv.Point2f; end: cv.Point2f }, roi: cv.Rect, frame: cv.Mat): Line => {
    const lineDir = new Line();
    lineDir.calculateDirection(untrimmed);
    const dir = lineDir.direction;

    const lineValues: number[] = new Array(roi.height)
    const prepDirection = new cv.Point(-dir.y, dir.x);
    let i = 0;
    for (let x = roi.x; x < (roi.x + roi.width); x++, i++) {
      const curPoint = this.pointByX(untrimmed, x);
      lineValues[i] = this.getLineSumValue(frame, curPoint, prepDirection, this.settings.AutoWidth_SumCount, roi);
    }
    const trimPoint = this.trimArray(lineValues, this.settings.AutoWidth_SumThr);
    const p1 = this.pointByX(untrimmed, trimPoint.x + roi.x - 1);
    const p2 = this.pointByX(untrimmed, trimPoint.y + roi.x + 1);

    return new Line(this.bringPointToROI(p1, roi), this.bringPointToROI(p2, roi));
  }

  pointByPercent = (axis: { start: cv.Point2f; end: cv.Point2f }, percent: number): cv.Point2f => {
    const dist = Math.sqrt((axis.start.x - axis.end.x) * (axis.start.x - axis.end.x) + (axis.start.y - axis.end.y) * (axis.start.y - axis.end.y));
    const pdist = dist * percent;

    const lineDir = new Line();
    lineDir.calculateDirection(axis);
    const dir = lineDir.direction;

    const p = new cv.Point(dir.x * pdist + axis.start.x, dir.y * pdist + axis.start.y);
    return p;
  };

  pointByY(axis: { start: cv.Point2f, end: cv.Point2f }, Y: number) {
    const lineDir = new Line();
    lineDir.calculateDirection(axis);
    const dir = lineDir.direction;
    const dy = (Y - axis.start.y) / dir.y
    const p = new cv.Point(axis.start.x + dir.x * dy, axis.start.y + dir.y * dy);
    return p
  }

  pointByX(line: { start: cv.Point2f, end: cv.Point2f }, X: number) {

    return new cv.Point(X, this.YbyX(X, line))
  }

  trimArray(arr: number[], thr: number): cv.Point {
    const count = arr.length;
    let min = 0;
    let max = count - 1;
    for (let i = 0; i < count; i++) {
      if (arr[i] > thr) { min = i; break };
    }
    for (let i = count - 1; i > 0; i--) {
      if (arr[i] > thr) { max = i; break; }
    }

    return new cv.Point(min, max);
  }

  bringPointToROI(p: { x: number; y: number }, roi: cv.Rect) {

    let x = p.x;
    let y = p.y;

    if (x < roi.x) { x = roi.x };
    if (x > roi.x + roi.width) { x = roi.x + roi.width };
    if (y < roi.y) { y = roi.y };
    if (y > roi.y + roi.height) { y = roi.y + roi.height };

    return new cv.Point(x, y)
  }

  getLineSumValue(frame: cv.Mat, basePoint: cv.Point, perpDir: cv.Point, maxDist: number, roi: cv.Rect): number {
    let curPoint = basePoint;
    const step = perpDir;
    const stepM = new cv.Point(-perpDir.x, -perpDir.y);

    let sumValues = 0;

    let curX = Math.round(curPoint.x);
    let curY = Math.round(curPoint.y);

    while (this.isPointInsideROI({ x: curX, y: curY }, roi) && this.distancePoint(curPoint, basePoint) < maxDist) {
      sumValues += frame.ucharPtr(curY, curX)[0];
      curPoint = this.addPoint(curPoint, step);
      curX = Math.round(curPoint.x);
      curY = Math.round(curPoint.y);
    }

    curPoint = this.addPoint(basePoint, stepM);
    curX = Math.round(curPoint.x);
    curY = Math.round(curPoint.y);

    while (this.isPointInsideROI({ x: curX, y: curY }, roi) && this.distancePoint(curPoint, basePoint) < maxDist) {
      sumValues += frame.ucharPtr(curY, curX)[0];
      curPoint = this.addPoint(curPoint, stepM);
      curX = Math.round(curPoint.x);
      curY = Math.round(curPoint.y);
    }

    return sumValues;
  };

  distancePoint(p1: cv.Point, p2: cv.Point): number {
    return Math.sqrt(((p1.x - p2.x) * (p1.x - p2.x)) + ((p1.y - p2.y) * (p1.y - p2.y)));
  }

  addPoint(p1: cv.Point, p2: cv.Point): cv.Point {
    return new cv.Point(p1.x + p2.x, p1.y + p2.y)
  }

  isPointInsideROI(point: cv.Point, roi: { x: number; y: number; width: number; height: number }): boolean {
    const x = Math.floor(point.x);
    const y = Math.floor(point.y);
    return x >= roi.x && x <= roi.x + roi.width &&
      y >= roi.y && y <= roi.y + roi.height;
  }

  fitLine = (x: number[], y: number[]): { intercept: number; slope: number } => {
    const n = x.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) {
      sumX += x[i];
      sumY += y[i];
      sumXY += x[i] * y[i];
      sumXX += x[i] * x[i];
    }
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return { intercept, slope };
  };

  YbyX(x: number, line: { start: cv.Point, end: cv.Point }) {
    const p = line.start;

    const lineDir = new Line();
    lineDir.calculateDirection(line);
    const direction = lineDir.direction;

    return (x - p.x) / direction.x * direction.y + p.y;
  }

  getSumOfFrame = (frame: cv.Mat): number => {
    let sum = 0;
    for (let i = 0; i < frame.data.length; i++) {
      sum += frame.data[i];
    }
    return sum;
  }

  getMaxFrame = (frames: (cv.Mat | undefined)[]): cv.Mat | null => {
    const validFrames = frames.filter((f): f is cv.Mat => !!f);
    if (validFrames.length === 0) return null;

    let maxFrame = validFrames[0].clone();
    for (let i = 1; i < validFrames.length; i++) {
      cv.max(maxFrame, validFrames[i], maxFrame);
    }
    return maxFrame;
  }


  getMaxValue = (frame: cv.Mat) => {
    const result = cv.minMaxLoc(frame);
    return result.maxVal;
  }

  getTrimEnds = (frames: cv.Mat[]): { start: number; end: number } => {
    const trimTHR = 50;
    let start = 0;
    let end = frames.length - 1;

    while (start <= end && this.getSumOfFrame(frames[start]) < trimTHR) {
      start++;
    }
    while (end >= start && this.getSumOfFrame(frames[end]) < trimTHR) {
      end--;
    }

    return { start, end };
  }

  isRotated = (frames: cv.Mat[]): boolean => {
    if (frames.length === 0) return false;

    let COPs: cv.Point[] = [];
    frames.forEach(frame => {
      const center = this.ComputeCOP(frame);
      COPs.push(center);
    });

    const cnt = COPs.length;
    if (cnt < 6) return false;

    const copminy = (COPs[0].y + COPs[1].y + COPs[2].y) / 3.0;
    const copmaxy = (COPs[cnt - 1].y + COPs[cnt - 2].y + COPs[cnt - 3].y) / 3.0;

    if (copmaxy - copminy < 0) return false;
    else return true;
  }

  rotate180List(frames: cv.Mat[]): cv.Mat[] {
    return frames.map(frame => this.rotate180(frame));
  }

  rotate180(frame: cv.Mat): cv.Mat {
    const dst = new cv.Mat();
    cv.rotate(frame, dst, cv.ROTATE_180);
    return dst;
  }

  GetAllROISensors(ROI: cv.Rect) {
    let insidepoints: cv.Point[] = [];
    for (let x = ROI.x; x < (ROI.x + ROI.width); x++) {
      for (let y = ROI.y; y < (ROI.y + ROI.height); y++) {
        let p = new cv.Point(x, y);
        insidepoints.push(p);
      }
    }
    return insidepoints;
  }

  GetSumPressure(Sensors: cv.Point[], Frame: cv.Mat) {
    let count = 0;
    let sumPressure = 0;

    for (let sensor of Sensors) {
      if (sensor.y < Frame.rows && sensor.x < Frame.cols) {
        if (Frame.ucharPtr(sensor.y, sensor.x)[0] > this.settings.Surface_Thr) {
          count++;
          sumPressure += Frame.ucharPtr(sensor.y, sensor.x)[0] / this.pressureToIntScale;
        }
      }
    }
    return sumPressure;
  }

  GetAveragePressure(sensors: cv.Point[], frame: cv.Mat) {
    let count = 0;
    let sumPressure = 0;

    for (const sensor of sensors) {
      if (sensor.y < frame.rows && sensor.x < frame.cols) {
        if (frame.ucharPtr(sensor.y, sensor.x)[0] > this.settings.Surface_Thr) {
          count++;
          sumPressure += frame.ucharPtr(sensor.y, sensor.x)[0] / this.pressureToIntScale;
        }
      }
    }
    return count === 0 ? 0 : (sumPressure / count);
  }

  GetSurface(sensors: cv.Point[], frame: cv.Mat) {
    const sensorArea = (this.sensorWidth * this.sensorHeight) / 100.0;
    let count = 0;

    for (const sensor of sensors) {
      if (frame.ucharPtr(sensor.y, sensor.x)[0] > this.settings.Surface_Thr) {
        count++;
      }
    }
    return sensorArea * count;
  }

  GetCenterOfPressure(sensors: cv.Point[], frame: cv.Mat, capPressure: number): cv.Point {
    let count = 0;
    let sumPressure = 0;
    let centerOfPressure = new cv.Point(0, 0);

    let maxPressure = 0;
    for (const sensor of sensors) {
      const val = frame.ucharPtr(sensor.y, sensor.x)[0];
      if (val > maxPressure) maxPressure = val;
    }

    for (const sensor of sensors) {
      if (sensor.y < frame.rows && sensor.x < frame.cols) {
        const val = frame.ucharPtr(sensor.y, sensor.x)[0];
        if (val > this.settings.Surface_Thr) {
          let pval = val;
          if (pval > maxPressure * capPressure) pval = maxPressure * capPressure;
          centerOfPressure.x += sensor.x * pval;
          centerOfPressure.y += sensor.y * pval;
          count++;
          sumPressure += pval;
        }
      }
    }

    if (sumPressure !== 0) {
      centerOfPressure.x = centerOfPressure.x / sumPressure;
      centerOfPressure.y = centerOfPressure.y / sumPressure;
    }

    if (count === 0) return new cv.Point(0, 0);
    return centerOfPressure;
  }

  GetCenterOfPressureMinThr(sensors: cv.Point[], frame: cv.Mat, capPressure: number, thr: number): cv.Point {
    let count = 0;
    let sumPressure = 0;
    let centerOfPressure = new cv.Point(0, 0);

    let maxPressure = 0;
    for (const sensor of sensors) {
      const val = frame.ucharPtr(sensor.y, sensor.x)[0];
      if (val > maxPressure) maxPressure = val;
    }

    for (const sensor of sensors) {
      if (sensor.y < frame.rows && sensor.x < frame.cols) {
        const val = frame.ucharPtr(sensor.y, sensor.x)[0];
        if (val > maxPressure * thr) {
          let pval = val;
          if (pval > maxPressure * capPressure) pval = maxPressure * capPressure;
          centerOfPressure.x += sensor.x * pval;
          centerOfPressure.y += sensor.y * pval;
          count++;
          sumPressure += pval;
        }
      }
    }

    if (sumPressure !== 0) {
      centerOfPressure.x = centerOfPressure.x / sumPressure;
      centerOfPressure.y = centerOfPressure.y / sumPressure;
    }

    if (count === 0) return new cv.Point(0, 0);
    return centerOfPressure;
  }

  GetCOPArray(Frames: cv.Mat[], ROI: cv.Rect) {
    var COPArray: cv.Point[] = [];
    for (var f of Frames) {
      const COP = this.ComputeCOP(f, ROI);
      COPArray.push(COP);
    }
    return COPArray;
  }

  GetAllFootPoints(MaxFrame: cv.Mat, ROI: cv.Rect) {
    const FootPoints: cv.Point[] = [];

    for (var x = ROI.y; x < (ROI.y + ROI.height); x++) {
      for (var y = ROI.x; y < (ROI.x + ROI.width); y++) {
        if (MaxFrame.ucharPtr(x, y)[0] > 0)
          FootPoints.push(new cv.Point(y, x));
      }
    }
    return FootPoints;
  }

  Get2LongestLineSegments(LineSegments: Line[]) {
    var BorderLines: Line[] = [];
    const LongestLines = LineSegments.slice().sort((a, b) => a.len - b.len);
    var LastThreeLines: Line[] = [];
    var flag = 0;
    LastThreeLines = LongestLines.slice(LongestLines.length - 3, LongestLines.length);
    BorderLines.push(LastThreeLines[2]);

    if ((LastThreeLines[0].start.y + LastThreeLines[0].end.y > LastThreeLines[1].start.y + LastThreeLines[1].end.y) && ((LastThreeLines[0].len < LastThreeLines[1].len * 1.2) && (LastThreeLines[0].len > LastThreeLines[1].len * 0.8))) {
      BorderLines.push(LastThreeLines[0]);
      flag = 0;
    }
    else {
      BorderLines.push(LastThreeLines[1]);
      flag = 1;
    }

    if ((BorderLines[0].start == BorderLines[1].start) || (BorderLines[0].start == BorderLines[1].end) || (BorderLines[0].end == BorderLines[1].start) || (BorderLines[0].end == BorderLines[1].end)) {
      if (flag == 0) {
        BorderLines[0] = LastThreeLines[2];
        BorderLines[1] = LastThreeLines[1];
      }
      else {
        BorderLines[0] = LastThreeLines[2];
        BorderLines[1] = LastThreeLines[0];
      }
    }
    return BorderLines;
  }

  GetLineSegmentOfPolygon(PolyPoints: cv.Point[]) {
    const lines: Line[] = [];

    for (let i = 0; i < PolyPoints.length; i++) {
      const p1 = PolyPoints[i];
      const p2 = PolyPoints[(i + 1) % PolyPoints.length];

      lines.push(new Line(p1, p2));
    }

    return lines;
  }

  GetHeelPoints(FootPoint: cv.Point[], FootRec: cv.Rect) {
    var HeelPoints: cv.Point[] = [];
    const HeelRec = this.GetHeelRectangleVertical(FootRec);
    for (var i = 0; i < FootPoint.length; i++) {
      if (this.isPointInsideROI(FootPoint[i], HeelRec) == true) {
        HeelPoints.push(FootPoint[i]);
      }
    }

    return HeelPoints;
  }

  FindMinimalBoundingCircle(points: cv.Point[]) {
    const hull = this.convexHull(points);

    let bestCenter = new cv.Point(hull[0].x, hull[0].y);
    let bestRadius2 = Number.POSITIVE_INFINITY;

    // Helper: check if circle encloses all points
    function circleEncloses(center: cv.Point, radius2: number, pts: cv.Point[], skip1: number, skip2: number, skip3: number) {
      for (let i = 0; i < pts.length; i++) {
        if (i === skip1 || i === skip2 || i === skip3) continue;
        const dx = pts[i].x - center.x;
        const dy = pts[i].y - center.y;
        if (dx * dx + dy * dy > radius2 + 1e-6) return false;
      }
      return true;
    }

    // Helper: find circle through 3 points
    function findCircle(p1: cv.Point, p2: cv.Point, p3: cv.Point) {
      const A = p2.x - p1.x, B = p2.y - p1.y;
      const C = p3.x - p1.x, D = p3.y - p1.y;
      const E = A * (p1.x + p2.x) + B * (p1.y + p2.y);
      const F = C * (p1.x + p3.x) + D * (p1.y + p3.y);
      const G = 2 * (A * (p3.y - p2.y) - B * (p3.x - p2.x));
      if (Math.abs(G) < 1e-6) return { center: { x: 0, y: 0 }, radius2: Number.POSITIVE_INFINITY };
      const cx = (D * E - B * F) / G;
      const cy = (A * F - C * E) / G;
      const dx = cx - p1.x, dy = cy - p1.y;
      return { center: { x: cx, y: cy }, radius2: dx * dx + dy * dy };
    }

    // Pairs
    for (let i = 0; i < hull.length - 1; i++) {
      for (let j = i + 1; j < hull.length; j++) {
        const testCenter = {
          x: (hull[i].x + hull[j].x) / 2,
          y: (hull[i].y + hull[j].y) / 2
        };
        const dx = testCenter.x - hull[i].x;
        const dy = testCenter.y - hull[i].y;
        const testRadius2 = dx * dx + dy * dy;
        if (testRadius2 < bestRadius2) {
          if (circleEncloses(testCenter, testRadius2, hull, i, j, -1)) {
            bestCenter = testCenter;
            bestRadius2 = testRadius2;
          }
        }
      }
    }

    // Triples
    for (let i = 0; i < hull.length - 2; i++) {
      for (let j = i + 1; j < hull.length - 1; j++) {
        for (let k = j + 1; k < hull.length; k++) {
          const { center: testCenter, radius2: testRadius2 } = findCircle(hull[i], hull[j], hull[k]);
          if (testRadius2 < bestRadius2) {
            if (circleEncloses(testCenter, testRadius2, hull, i, j, k)) {
              bestCenter = testCenter;
              bestRadius2 = testRadius2;
            }
          }
        }
      }
    }

    const radius = bestRadius2 === Number.POSITIVE_INFINITY ? 0 : Math.sqrt(bestRadius2);
    return bestCenter;
  }

  GetHeelRectangleVertical(FootRec: cv.Rect) {
    const HeelPercent = 0.3;
    const HeelRec = new cv.Rect(FootRec.x, Math.round((FootRec.y + FootRec.height) - (FootRec.height * HeelPercent)), FootRec.width, Math.round(FootRec.height * HeelPercent));

    return HeelRec;
  }

  GetExteriorAngleDegree = (line1: Line, line2: Line) => {
    const direction: cv.Point = line1.direction;
    const direction2: cv.Point = line2.direction;

    var angle = (Math.atan2(direction2.y, direction2.x) - Math.atan2(direction.y, direction.x)) * (180.0 / Math.PI);
    if (!(angle <= -180.0)) {
      if (!(angle > 180.0)) {
        return angle;
      }

      return angle - 360.0;
    }

    return angle + 360.0;
  }

  GetHullAxisLine(maxFrame: cv.Mat, roi: cv.Rect, cops: cv.Point[], longestLines: Line[], heelCenter: cv.Point, LorR: string) {
    const l1 = longestLines[0];
    const l2 = longestLines[1];

    let angle = this.GetExteriorAngleDegree(l1, l2);

    if (angle < 0 && angle > -90) angle = -angle;
    else if (angle < -90 && angle > -180) angle = 180 + angle;
    else if (angle < 180 && angle > 90) angle = 180 - angle;

    if (angle === 180 || angle === 0) {
      return this.getAxisLine(cops, roi, maxFrame);
    }

    let mAxis;
    let m1;
    let m2;
    let Teta;

    const L1 = {
      P1: { X: l1.start.x, Y: l1.start.y },
      P2: { X: l1.end.x, Y: l1.end.y }
    };
    const L2 = {
      P1: { X: l2.start.x, Y: l2.start.y },
      P2: { X: l2.end.x, Y: l2.end.y }
    };

    if (LorR === "Left") {
      if (L1.P1.X + L1.P2.X > L2.P1.X + L2.P2.X) {
        if (L1.P2.X - L1.P1.X === 0) {
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = 1 / Math.tan(Teta);
        } else if (Math.abs((L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X) - (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X)) > 0.5) {
          m1 = (L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X);
          m2 = (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X);
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = (m1 - Math.tan(Teta)) / (1 + (m1 * Math.tan(Teta)));
        } else if (Math.abs((L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X) - (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X)) < 0.5) {
          m1 = (L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X);
          m2 = (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X);
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = (m1 + Math.tan(Teta)) / (1 - (m1 * Math.tan(Teta)));
        }
      } else if (L1.P1.X + L1.P2.X < L2.P1.X + L2.P2.X) {
        if (L2.P2.X - L2.P1.X === 0) {
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = 1 / Math.tan(Teta);
        } else if (Math.abs((L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X) - (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X)) > 0.5) {
          m1 = (L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X);
          m2 = (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X);
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = (m2 - Math.tan(Teta)) / (1 + (m2 * Math.tan(Teta)));
        } else if (Math.abs((L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X) - (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X)) < 0.5) {
          m1 = (L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X);
          m2 = (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X);
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = (m2 + Math.tan(Teta)) / (1 - (m2 * Math.tan(Teta)));
        }
      }
    } else if (LorR === "Right") {
      if ((L1.P1.X + L1.P2.X) < (L2.P1.X + L2.P2.X)) {
        if (L1.P2.X - L1.P1.X === 0) {
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = -1 / Math.tan(Teta);
        } else {
          m1 = (L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X);
          m2 = (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X);
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = (m1 + Math.tan(Teta)) / (1 - (m1 * Math.tan(Teta)));
        }
      } else if ((L2.P1.X + L2.P2.X) < (L1.P1.X + L1.P2.X)) {
        if (L2.P2.X - L2.P1.X === 0) {
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = -1 / Math.tan(Teta);
        } else {
          m1 = (L1.P2.Y - L1.P1.Y) / (L1.P2.X - L1.P1.X);
          m2 = (L2.P2.Y - L2.P1.Y) / (L2.P2.X - L2.P1.X);
          Teta = (0.4 * angle) * (Math.PI / 180);
          mAxis = (m2 + Math.tan(Teta)) / (1 - (m2 * Math.tan(Teta)));
        }
      }
    }

    if (mAxis == undefined)
      return new Line();

    const intersection = this.GetIntersectionofLineSegments(l1, l2);
    const bpar = intersection.y - (mAxis * intersection.x);
    const fitLine = { b: bpar, m: mAxis };

    const Y0 = roi.y + roi.height;
    const X0 = (Y0 - fitLine.b) / fitLine.m;
    const Y1 = roi.y;
    const X1 = (Y1 - fitLine.b) / fitLine.m;

    const untrimmed = new Line(new cv.Point(X0, Y0), new cv.Point(X1, Y1));
    const axisLine = this.trimAxisLine(untrimmed, roi, maxFrame);
    const newUntrimmed = new Line(axisLine.start, heelCenter);
    const newAxisLine = this.trimAxisLine(newUntrimmed, roi, maxFrame);

    return newAxisLine;
  }

  GetIntersectionofLineSegments(L1: Line, L2: Line) {
    const dx12 = L1.end.x - L1.start.x;
    const dy12 = L1.end.y - L1.start.y;
    const dx34 = L2.end.x - L2.start.x;
    const dy34 = L2.end.y - L2.start.y;

    const denominator = (dy12 * dx34 - dx12 * dy34);
    if (denominator === 0) {
      // Lines are parallel, return null or some default
      return { x: NaN, y: NaN };
    }
    const t1 = ((L1.start.x - L2.start.x) * dy34 + (L2.start.y - L1.start.y) * dx34) / denominator;

    return new cv.Point(L1.start.x + dx12 * t1, L1.start.y + dy12 * t1);
  }

  GetHeelLine(Axis: Line, ROI: cv.Rect, MaxFrame: cv.Mat) {
    const Midpoint = this.pointByPercent(Axis, 0.85);
    const Direction = new cv.Point(-Axis.direction.y, Axis.direction.x);
    const untrimmed = this.getLineTrimmedByRoi(Direction, Midpoint, ROI);
    const Trimmed = this.trimWidthLine(untrimmed, ROI, MaxFrame);
    return Trimmed;
  }

  GetMetatarsalLine(newAxisLine: Line, longestLines: Line[], LorR: string) {
    const l1 = longestLines[0];
    const l2 = longestLines[1];

    let newMetLine;
    let pup, centerp;

    if (newAxisLine.start.y > newAxisLine.end.y) {
      pup = newAxisLine.end;
      centerp = newAxisLine.start;
    } else {
      pup = newAxisLine.start;
      centerp = newAxisLine.end;
    }

    let angle = this.GetExteriorAngleDegree(l1, l2);
    if (angle < 0 && angle > -90) angle = -angle;
    else if (angle < -90 && angle > -180) angle = 180 + angle;
    else if (angle < 180 && angle > 90) angle = 180 - angle;

    if (angle === 180 || angle === 0) {
      const lineDis = Math.abs(l1.start.x - l2.start.x) * 0.2;
      if (LorR === "Left") {
        newMetLine = new Line(new cv.Point(pup.x + lineDis, pup.y), centerp);
      } else {
        newMetLine = new Line(new cv.Point(pup.x - lineDis, pup.y), centerp);
      }
      return newMetLine;
    }

    angle = 0.2 * angle;

    if (LorR === "Left") {
      newMetLine = new Line(this.rotatePoint(pup, centerp, angle), centerp);
    } else {
      newMetLine = new Line(this.rotatePoint(pup, centerp, -angle), centerp);
    }

    return newMetLine;
  }

  rotatePoint = (pointToRotate: cv.Point, centerPoint: cv.Point, angleInDegrees: number) => {
    const angleInRadians = angleInDegrees * (Math.PI / 180);
    const cosTheta = Math.cos(angleInRadians);
    const sinTheta = Math.sin(angleInRadians);
    return new cv.Point(
      cosTheta * (pointToRotate.x - centerPoint.x) - sinTheta * (pointToRotate.y - centerPoint.y) + centerPoint.x,
      sinTheta * (pointToRotate.x - centerPoint.x) + cosTheta * (pointToRotate.y - centerPoint.y) + centerPoint.y
    );
  };

  GetZones(Axis: Line, WidthLine: Line, HeelLine: Line, ROI: cv.Rect, Foot: string) {
    var isRight = Foot == "Right";

    const zp = new zoneProcessor();
    zp.autoMatch(ROI, Axis, WidthLine, HeelLine, isRight);

    return zp;
  }

  GetZonesByControlPoints(cPoints: cv.Point[], WidthLine: Line, HeelLine: Line, ROI: cv.Rect, Foot: string) {
    var isRight = Foot == "Right";

    const zp = new zoneProcessor();
    zp.autoMatchByControlPoints(ROI, cPoints, WidthLine, HeelLine, isRight);

    return zp;
  }

  GetForcePressureRatio() {
    return (this.sensorHeight * this.sensorWidth / 100.0) * 10.0;
  }

  convexHull(points: cv.Point[]) {
    if (points.length <= 1) {
      return [...points];
    }

    let upperHull = [];
    for (const p of points) {
      while (upperHull.length >= 2) {
        const q = upperHull[upperHull.length - 1];
        const r = upperHull[upperHull.length - 2];

        const crossProduct = (q.x - r.x) * (p.y - r.y) - (q.y - r.y) * (p.x - r.x);
        if (crossProduct >= 0) {
          upperHull.pop();
        } else {
          break;
        }
      }
      upperHull.push(p);
    }

    upperHull.pop();

    let lowerHull = [];
    for (let i = points.length - 1; i >= 0; i--) {
      const p = points[i];

      while (lowerHull.length >= 2) {
        const q = lowerHull[lowerHull.length - 1];
        const r = lowerHull[lowerHull.length - 2];

        const crossProduct = (q.x - r.x) * (p.y - r.y) - (q.y - r.y) * (p.x - r.x);
        if (crossProduct >= 0) {
          lowerHull.pop();
        } else {
          break;
        }
      }
      lowerHull.push(p);
    }

    lowerHull.pop();

    if (!(upperHull.length === 1 && lowerHull.length === 1 && upperHull[0].x === lowerHull[0].x && upperHull[0].y === lowerHull[0].y)) {
      upperHull = upperHull.concat(lowerHull);
    }

    return upperHull;
  }

  GetVisibleZoneSensors(MaxFrame: cv.Mat, ZonePoints: cv.Point[]) {
    const visibles: cv.Point[] = [];
    for (const p of ZonePoints) {
      if (MaxFrame.charPtr(p.y, p.x)[0] > this.settings.ZoneSensorVisibleThr) {
        visibles.push(p);
      }
    }
    return visibles;
  }

}